<script setup lang="ts">
import { createOrder, getOrder, updateOrder } from '@/api/order/api';
import { CreateOrderDto, Order, UpdateOrderDto } from '@/api/order/types';
import { getUsers } from '@/api/user/api';
import { getErrorMessage } from '@/helpers/error-handler';
import { useToastStore } from '@/stores/toast.store';
import { VueformElement } from '@vueform/vueform';
import _ from 'lodash';
import { Ref, onMounted, ref } from 'vue';
import Avatar from 'vue-boring-avatars';

const props = defineProps<{
  order: number | undefined;
}>();

const editMode = ref(props.order !== undefined);

interface FormData extends Omit<Order, 'assignedTo' | 'installedBy'> {
  mode: 'edit' | 'create';
  assignedTo: number[];
  installedBy: number | null;
}

const toast = useToastStore();

const emit = defineEmits(['finished']);

const form$: Ref<VueformElement | null> = ref(null);
const users = ref<{ value: number; label: string }[]>([]);
const initialOrder: Ref<FormData | null> = ref(null);

const getDiff = (order: FormData) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const changes = (object: any, base: any) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return _.transform(object, (result: any, value: any, key: any) => {
      if (!_.isEqual(value, base[key])) {
        result[key] = _.isObject(value) && _.isObject(base[key]) ? changes(value, base[key]) : value;
      }
    });
  };
  return changes(order, initialOrder.value);
};

const upsertData = async (form$: VueformElement) => {
  const data = form$.data as FormData;

  form$.submitting = true;

  try {
    if (data.mode === 'create') {
      await createOrder(data as CreateOrderDto);
    } else {
      if (!props.order) {
        throw new Error('Order ID is required for update');
      }
      const diff = getDiff(data);
      await updateOrder(props.order, diff as UpdateOrderDto);
    }
    toast.addToast({
      title: `Votre commande a bien été ${data.mode === 'create' ? 'ajoutée' : 'modifiée'}`,
      message: `Votre commande a bien été ${data.mode === 'create' ? 'ajoutée' : 'modifiée'}`,
      type: 'success',
      duration: 2000,
    });
  } catch (error) {
    console.error('error', error);
    toast.addToast({
      title: "Impossible d'ajouter/modifier une commande",
      message: getErrorMessage(error),
      type: 'danger',
      duration: 5000,
    });
    return;
  } finally {
    form$.submitting = false;
    form$.reset();
    emit('finished');
  }
};

onMounted(async () => {
  const { data } = await getUsers();
  users.value = data.map((user) => ({
    value: user.id,
    label: user.email,
  }));

  if (props.order) {
    const { data } = await getOrder(props.order);
    const order = {
      mode: 'edit',
      ...data,
      installedBy: data.installedBy?.id,
      assignedTo: data.assignedTo.map((user) => user.id),
    } as FormData;
    initialOrder.value = order;
    form$.value?.update(order);
  }
});
</script>

<template>
  <Vueform ref="form$" size="md" :endpoint="false" @submit="upsertData">
    <HiddenElement name="mode" :default="'create'" />
    <GroupElement name="container">
      <GroupElement name="left-container" :columns="{ container: 6 }">
        <TextElement name="clientEmail" input-type="email" :rules="['required', 'email']" label="Email client" />
        <ToggleElement name="isFulfilled" :conditions="[['mode', 'in', ['edit']]]"> Installée </ToggleElement>
        <DateElement name="installedAt" label="Date d'installation" :date="true" :time="true" :conditions="[['mode', 'in', ['edit']]]" />
        <TextElement name="installedHeadsetSerial" input-type="text" label="Serial du casque" :conditions="[['mode', 'in', ['edit']]]" />
        <SelectElement
          name="installedBy"
          :items="users"
          :search="true"
          :native="false"
          label="Installée par"
          input-type="search"
          autocomplete="off"
          :conditions="[['mode', 'in', ['edit']]]"
        />
        <MultiselectElement
          name="assignedTo"
          :items="users"
          :search="true"
          :native="false"
          label="Assignée à"
          input-type="search"
          autocomplete="off"
        >
          <template #option="{ option }: { option: { value: number; label: string } }">
            <!-- <img class="rounded-full w-6 h-6 mr-1.5" :src="option.image"> -->
            <Avatar class="me-2" :size="32" variant="beam" :name="option.label" />
            {{ option.label }}
          </template>
          <template #multiple-label="{ values }: { values: { value: number; label: string }[] }">
            <div class="d-flex flex-row w-100">
              <div class="avatars">
                <Avatar v-for="value in values" :key="value.value" class="me-2" :size="32" variant="beam" :name="value.label" />
              </div>
            </div>
          </template>
        </MultiselectElement>
      </GroupElement>
      <GroupElement name="right-container" :columns="{ container: 6 }">
        <ObjectElement name="procedure">
          <SelectElement
            name="procedure"
            :rules="['required']"
            :items="[
              {
                value: 'Opticien',
                label: 'Opticien',
              },
              {
                value: 'Ortho',
                label: 'Ortho',
              },
              {
                value: 'Ortho homme',
                label: 'Ortho Home',
              },
              {
                value: 'Oculera',
                label: 'Oculera',
              },
              {
                value: 'Krys',
                label: 'Krys',
              },
              {
                value: 'CDL',
                label: 'CDL',
              },
              {
                value: 'MIA',
                label: 'MIA',
              },
            ]"
            :search="true"
            :native="false"
            label="Procédure"
            input-type="search"
            autocomplete="off"
          />
          <SelectElement
            name="environment"
            :rules="['required']"
            :items="[
              {
                value: 'prod',
                label: 'Prod',
              },
              {
                value: 'beta',
                label: 'Beta',
              },
              {
                value: 'recette',
                label: 'Recette',
              },
            ]"
            :search="true"
            :native="false"
            label="Environement"
            input-type="search"
            autocomplete="off"
          />
          <ToggleElement name="preview"> Mode preview </ToggleElement>
          <ObjectElement name="properties">
            <GroupElement name="krys" :conditions="[['container.right-container.procedure.procedure', 'in', ['Krys', 'CDL']]]">
              <TextElement name="krys_client_code" label="Code Client" :rules="['regex:/^\\d{6}$/m']" />
              <TextElement name="krys_wifi_ssid" label="WiFI SSID" />
              <TextElement name="krys_wifi_password" input-type="password" label="WiFi Password" />
            </GroupElement>
            <GroupElement name="eyesoft" :conditions="[['container.right-container.procedure.procedure', 'in', ['Opticien', 'Ortho']]]">
              <TextElement name="eyesoft_code_client" label="Code Client" />
            </GroupElement>
            <GroupElement name="oculera" :conditions="[['container.right-container.procedure.procedure', 'in', ['Oculera']]]">
              <SelectElement
                name="oculera_verson"
                :items="['2.3.0', '2.6.4', 'latest']"
                default="latest"
                :search="true"
                :native="false"
                label="Version"
                input-type="search"
                autocomplete="off"
              />
            </GroupElement>
            <GroupElement name="mia" :conditions="[['container.right-container.procedure.procedure', 'in', ['MIA']]]">
              <SelectElement
                name="product"
                :rules="['required']"
                :items="[
                  {
                    value: 'evosight',
                    label: 'Evosight',
                  },
                  {
                    value: 'visionlab',
                    label: 'VisionLab',
                  },
                  {
                    value: 'opticien',
                    label: 'Check Lenscape',
                  },
                ]"
                :search="true"
                :native="false"
                label="Product"
                input-type="search"
                autocomplete="off"
                default="evosight"
              />
              <TextElement
                name="mia_sender_bundle_id"
                label="Sender Bundle ID"
                expression="{MIA_BUNDLE_ID(procedure.properties.product)}"
                readonly
              />
              <TextElement name="mia_product_small_name" label="Product Small Name" expression="{procedure.properties.product}" readonly />
              <TextElement
                name="mia_api_uri"
                label="API URI"
                expression="{MIA_API(procedure.properties.product, procedure.environment)}"
                readonly
              />
              <TextElement name="mia_target_pui_version" label="Target PUI Version" default="5.9.9.0" />
              <TextElement name="mia_language" label="Language" default="fr" />
            </GroupElement>
          </ObjectElement>
        </ObjectElement>
      </GroupElement>
      <ButtonElement name="submit" :button-label="editMode ? 'Modifier' : 'Ajouter'" :submits="true" align="right" />
    </GroupElement>
  </Vueform>
</template>

<style scoped>
.avatars {
  margin-left: 1em;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(10px, max-content));
}
</style>
